import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { mapCognitoError } from '../utils/helpers';
import Logo from '../components/Logo';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
  },
  logo: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#01696F',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  subtitle: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '2rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  btn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#01696F',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1.5rem',
    fontSize: '0.875rem',
    color: '#64748b',
  },
  footerLink: {
    color: '#01696F',
    fontWeight: '500',
    textDecoration: 'none',
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '1rem',
    color: '#01696F',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '0',
  },
};

export default function ForgotPasswordPage() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setStep('confirm');
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      navigate('/login', { state: { flash: 'Password reset! Please sign in.' } });
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
          <div style={styles.subtitle}>Reset your password</div>

          {error && <div style={styles.error}>{error}</div>}

          <button
            type="button"
            style={styles.backLink}
            onClick={() => { setStep('email'); setError(''); }}
          >
            ← Back
          </button>

          <form onSubmit={handleConfirm}>
            <div style={styles.formGroup}>
              <label htmlFor="forgot-code" style={styles.label}>Verification Code</label>
              <input
                id="forgot-code"
                style={styles.input}
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="Enter code from your email"
                required
                autoFocus
              />
            </div>
            <div style={styles.formGroup}>
              <label htmlFor="forgot-new-password" style={styles.label}>New Password</label>
              <input
                id="forgot-new-password"
                style={styles.input}
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="New password (min 8 characters)"
                required
                minLength={8}
              />
            </div>
            <div style={styles.formGroup}>
              <label htmlFor="forgot-confirm-password" style={styles.label}>Confirm New Password</label>
              <input
                id="forgot-confirm-password"
                style={styles.input}
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={8}
              />
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div style={styles.footer}>
            Remembered your password?{' '}
            <Link to="/login" style={styles.footerLink}>Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
        <div style={styles.subtitle}>Enter your email to reset your password</div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSendCode}>
          <div style={styles.formGroup}>
            <label htmlFor="forgot-email" style={styles.label}>Email</label>
            <input
              id="forgot-email"
              style={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>

        <div style={styles.footer}>
          Remembered your password?{' '}
          <Link to="/login" style={styles.footerLink}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}
